import { IFAQ } from "./faq";

export type IPosProvider =
  | "loman"
  | "toast"
  | "square"
  | "clover"
  | "kwick"
  | "stream"
  | "foodtec"
  | "spoton";

export interface IAgentSettings {
  id: string;
  name: string;
  type: string;
  address: string | null;
  allowAnonymousCalls: boolean;
  ambientNoise: boolean;
  ambientNoiseVolume: number;
  voiceId: string;
  voiceSpeed: number;
  phoneNumber: E164Number | null;
  fallbackPhoneNumber: E164Number | null;
  greetingMessageOpen: string | null;
  greetingMessageClosed: string | null;
  greetingMessageTempClosed: string | null;
  schedule: ISchedule | null;
  orderingHours: ISchedule | null;
  tempClosedUntil: string | null;
  faqs: IFAQ[];
  orderingConfig: IOrderingConfig | null;
  posProvider?: IPosProvider | null;
  stripeAccountId?: string | null;
  stripeChargesEnabled?: boolean | null;
  streamConnectedAt?: string | null;
  deliveryPrepTime: string | null;
  pickupPrepTime: string | null;
  customPrepTimeEnabled: boolean;
  customPrepTimeHours: IPrepTimeSchedule;
}
export interface ISchedule {
  monday: ITimeSlot[];
  tuesday: ITimeSlot[];
  wednesday: ITimeSlot[];
  thursday: ITimeSlot[];
  friday: ITimeSlot[];
  saturday: ITimeSlot[];
  sunday: ITimeSlot[];
}
export type DayOfWeek = keyof ISchedule;
export type TimeString = `${number}:${number}`; // enforces "HH:MM" format
export interface ITimeSlot {
  id: string;
  open: TimeString | null;
  close: TimeString | null;
}
export interface IPrepTimeSlot extends ITimeSlot {
  delivery: string | null;
  pickup: string | null;
}

export interface IPrepTimeSchedule {
  monday: IPrepTimeSlot[];
  tuesday: IPrepTimeSlot[];
  wednesday: IPrepTimeSlot[];
  thursday: IPrepTimeSlot[];
  friday: IPrepTimeSlot[];
  saturday: IPrepTimeSlot[];
  sunday: IPrepTimeSlot[];
}
export interface IOrderingConfig {
  prepTime?: string;
  allowScheduledOrders: IAllowScheduledOrdersOption;
  orderUntilClosed: boolean;
  shouldTotal: boolean;
  exactTimeEstimate: boolean;
  pickupModuleConfig: IPickupModuleConfig | null;
  deliveryModuleConfig?: IDeliveryModuleConfig | null;
  dynamicPrepTime?: boolean; // use POS-provided dynamic prep estimates when available (Toast only for now)
}
export interface IPickupModuleConfig {
  enabled: boolean;
  paymentModuleConfig: PaymentModuleConfig;
}
export interface IDeliveryModuleConfig {
  enabled: boolean;
  paymentModuleConfig: PaymentModuleConfig;
}

export const PaymentProvider = {
  STRIPE: "stripe",
  NONE: "none",
} as const;
export type PaymentProvider =
  (typeof PaymentProvider)[keyof typeof PaymentProvider];
export const PaymentStrategy = {
  REQUIRED: "required",
  REQUIRED_ABOVE: "required_above",
} as const;
export type PaymentStrategy =
  (typeof PaymentStrategy)[keyof typeof PaymentStrategy];
export type PaymentModuleConfig =
  | {
      provider: typeof PaymentProvider.STRIPE;
      strategy: typeof PaymentStrategy.REQUIRED;
      requireTip: boolean;
    }
  | {
      provider: typeof PaymentProvider.STRIPE;
      strategy: typeof PaymentStrategy.REQUIRED_ABOVE;
      requiredAboveThreshold: number;
      requireTip: boolean;
    }
  | {
      provider: typeof PaymentProvider.NONE;
    };

export type IAllowScheduledOrdersOption =
  | "always"
  | "never"
  | "normal"
  | "morning";

export const emptySchedule: ISchedule = {
  monday: [{ id: "monday-1", open: null, close: null }],
  tuesday: [{ id: "tuesday-1", open: null, close: null }],
  wednesday: [{ id: "wednesday-1", open: null, close: null }],
  thursday: [{ id: "thursday-1", open: null, close: null }],
  friday: [{ id: "friday-1", open: null, close: null }],
  saturday: [{ id: "saturday-1", open: null, close: null }],
  sunday: [{ id: "sunday-1", open: null, close: null }],
};

export const defaultSchedule: ISchedule = {
  monday: [{ id: "monday-1", open: "11:00", close: "22:00" }],
  tuesday: [{ id: "tuesday-1", open: "11:00", close: "22:00" }],
  wednesday: [{ id: "wednesday-1", open: "11:00", close: "22:00" }],
  thursday: [{ id: "thursday-1", open: "11:00", close: "22:00" }],
  friday: [{ id: "friday-1", open: "11:00", close: "22:00" }],
  saturday: [{ id: "saturday-1", open: "11:00", close: "22:00" }],
  sunday: [{ id: "sunday-1", open: "11:00", close: "22:00" }],
};

export const defaultAgentSettings: IAgentSettings = {
  id: "",
  name: "",
  type: "",
  address: "",
  voiceId: "11labs-Marissa",
  voiceSpeed: 1.0,
  ambientNoise: true,
  ambientNoiseVolume: 1.0,
  phoneNumber: "",
  fallbackPhoneNumber: "",
  schedule: defaultSchedule,
  orderingHours: null,
  tempClosedUntil: null,
  allowAnonymousCalls: false,
  greetingMessageOpen: "",
  greetingMessageClosed: "",
  greetingMessageTempClosed: "",
  faqs: [],
  orderingConfig: null,
  posProvider: null,
  stripeAccountId: null,
  stripeChargesEnabled: null,
  streamConnectedAt: null,
  // New prep time related root fields
  deliveryPrepTime: null,
  pickupPrepTime: null,
  customPrepTimeEnabled: false,
  customPrepTimeHours: {
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
  },
};

export type E164Number = string;

export interface IChangedSettings
  extends Partial<Omit<IAgentSettings, "orderingConfig">> {
  orderingConfig?: Partial<IOrderingConfig> | null;
}

export interface ISettingsValidationErrors {
  greetingMessageOpen?: string;
  greetingMessageClosed?: string;
  schedule?: string;
  orderingHours?: string;
  prepTime?: string;
  fallbackPhoneNumber?: string;
  faqs?: string;
  pickupPrepTime?: string;
  deliveryPrepTime?: string;
  customPrepTime?: string;
}
